/**
 * Captured error information
 */
export interface CapturedError {
  message: string;
  stack?: string;
  timestamp: string;
}

/**
 * Error collector that captures JavaScript errors and console errors
 */
export class ErrorCollector {
  private errors: CapturedError[] = [];
  private consoleErrors: string[] = [];
  private maxErrors: number;
  private initialized = false;
  private originalConsoleError: typeof console.error | null = null;

  constructor(maxErrors: number = 5) {
    this.maxErrors = maxErrors;
  }

  /**
   * Initialize error listeners
   */
  initialize(): void {
    if (this.initialized || typeof window === 'undefined') return;

    this.setupGlobalErrorHandler();
    this.setupUnhandledRejectionHandler();
    this.setupConsoleErrorInterceptor();
    this.initialized = true;
  }

  /**
   * Cleanup error listeners
   */
  cleanup(): void {
    if (!this.initialized || typeof window === 'undefined') return;

    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);

    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }

    this.initialized = false;
  }

  private handleError = (event: ErrorEvent): void => {
    this.addError({
      message: event.message,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    this.addError({
      message: `Unhandled Promise Rejection: ${reason?.message || String(reason)}`,
      stack: reason?.stack,
      timestamp: new Date().toISOString(),
    });
  };

  private setupGlobalErrorHandler(): void {
    window.addEventListener('error', this.handleError);
  }

  private setupUnhandledRejectionHandler(): void {
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  private setupConsoleErrorInterceptor(): void {
    this.originalConsoleError = console.error;
    const self = this;

    console.error = function (...args: unknown[]) {
      const errorMessage = args
        .map((arg) => {
          if (arg instanceof Error) {
            return `${arg.message}\n${arg.stack || ''}`;
          }
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      self.consoleErrors.push(errorMessage);
      if (self.consoleErrors.length > self.maxErrors) {
        self.consoleErrors.shift();
      }

      if (self.originalConsoleError) {
        self.originalConsoleError.apply(console, args);
      }
    };
  }

  private addError(error: CapturedError): void {
    this.errors.push(error);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }
  }

  /**
   * Get captured JavaScript errors
   */
  getErrors(): CapturedError[] {
    return [...this.errors];
  }

  /**
   * Get captured console.error messages
   */
  getConsoleErrors(): string[] {
    return [...this.consoleErrors];
  }

  /**
   * Clear all captured errors
   */
  clear(): void {
    this.errors = [];
    this.consoleErrors = [];
  }
}

/**
 * Redact sensitive data from text
 */
export function redactSensitiveData(text: string): string {
  let result = text;

  // Redact JWT tokens
  result = result.replace(
    /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
    '[JWT_REDACTED]'
  );

  // Redact API keys and secrets
  result = result.replace(
    /(?:api[_-]?key|apikey|token|secret|password|auth)[=:]["']?[\w-]{20,}["']?/gi,
    '[SENSITIVE_REDACTED]'
  );

  // Redact long base64 strings that look like credentials
  result = result.replace(/[A-Za-z0-9+/]{100,}={0,2}/g, '[BASE64_REDACTED]');

  return result;
}

/**
 * Sanitize URL by removing sensitive query parameters
 */
export function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['token', 'auth', 'key', 'password', 'secret', 'apikey', 'api_key'];

    sensitiveParams.forEach((param) => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });

    return urlObj.toString();
  } catch {
    return url;
  }
}
